import React, { useEffect, useMemo, useRef, useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { DateTime } from 'luxon'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import permissionService, {
	PERMISSIONS,
} from '@/core/api/services/permissionService'
import {
	isAccessDeniedError,
	shouldRetryRequest,
} from '@/core/api/utils/httpErrors'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { useCampaign } from '@/core/hooks/useCampaign'
import { useTranslation } from '@/core/i18n/useTranslation'
import type { RootState } from '@/core/store'
import {
	clearCampaignFlow,
	setCampaignFlow,
} from '@/core/store/slices/campaignFlowSlice'
import { Button } from '@/shared/components/atoms/Button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'

import { CampaignDetailsPageUI } from './CampaignDetailsPageUI'
import {
	campaignConfigSchema,
	type CampaignConfigFormValues,
} from './schemas/createCampaignSchema'
import { mapCampaignToConfigFormValues } from './utils/campaignToFormValues'
import { generateRRule } from './utils/rruleGenerator'

const CampaignDetailsPage: React.FC<{ readOnly?: boolean }> = ({
	readOnly,
}) => {
	const { t } = useTranslation('campaigns')
	const { t: tCommon } = useTranslation('common')
	const navigate = useNavigate()
	const location = useLocation()
	const dispatch = useDispatch()
	const { id } = useParams<{ id?: string }>()
	const isEdit = Boolean(id)
	const campaignId = isEdit && id ? Number(id) : undefined
	const isReadOnly =
		readOnly ??
		(Boolean(id) &&
			!location.pathname.includes('/edit') &&
			!location.pathname.includes('/create'))

	const canEditCampaign = permissionService.currentUserHasAnyPermission([
		PERMISSIONS.MANAGE_CHILDREN_ANNOUNCEMENTS,
		PERMISSIONS.MANAGE_OWN_LEVEL_ANNOUNCEMENTS,
		PERMISSIONS.UPDATE_ANNOUNCEMENTS,
	])
	const canDeleteCampaign = permissionService.currentUserHasAnyPermission([
		PERMISSIONS.MANAGE_CHILDREN_ANNOUNCEMENTS,
		PERMISSIONS.MANAGE_OWN_LEVEL_ANNOUNCEMENTS,
		PERMISSIONS.DESTROY_ANNOUNCEMENTS,
	])

	// Redux state
	const flowState = useSelector((state: RootState) => state.campaignFlow)

	const [currentRRule, setCurrentRRule] = useState<string | null>(null)
	const [accessDeniedModalOpen, setAccessDeniedModalOpen] = useState(false)
	const redirectToRef = useRef<string>('/campaigns')
	const [isFormInitialized, setIsFormInitialized] = useState<boolean>(
		!id // create mode is initialized immediately
	)

	useEffect(() => {
		if (!isEdit && flowState.mode === 'edit') {
			dispatch(clearCampaignFlow())
		}
	}, [dispatch, flowState.mode, isEdit])

	// Only fetch from API if:
	// 1. We're in edit mode AND
	// 2. Either Redux state is not initialized OR the campaignId doesn't match
	const shouldFetchFromAPI =
		isEdit &&
		(!flowState.isInitialized ||
			flowState.mode !== 'edit' ||
			flowState.campaignId !== campaignId)

	const {
		data: campaign,
		isFetching,
		error: campaignError,
	} = useCampaign({
		id: campaignId,
		config: useAccountConfig({}),
		options: {
			enabled: shouldFetchFromAPI,
			retry: shouldRetryRequest,
		},
	})

	// Warn before redirecting on 403/404 errors for campaign query
	useEffect(() => {
		if (!campaignError) return
		if (!isAccessDeniedError(campaignError)) return

		redirectToRef.current = '/campaigns'
		setAccessDeniedModalOpen(true)
	}, [campaignError])

	const defaultValues = useMemo<CampaignConfigFormValues>(() => {
		// If Redux state is initialized and matches our current mode/campaign,
		// use the Redux state (handles back navigation case)
		if (flowState.isInitialized && flowState.config) {
			if (!isEdit && flowState.mode === 'create') {
				return flowState.config
			}
			if (
				isEdit &&
				flowState.mode === 'edit' &&
				flowState.campaignId === campaignId
			) {
				return flowState.config
			}
		}

		if (isEdit) {
			// While the campaign is loading, keep minimal safe defaults.
			// Once the API data arrives we hydrate the form with the real
			// values from the backend (see useEffect below).
			return {
				name: '',
				rruleType: 'recurrent',
				startDate: DateTime.now().toJSDate(),
				endDate: DateTime.now().plus({ days: 7 }).toJSDate(),
				playEvery: 30,
				playEveryUnit: 'minutes',
				selectedDays: [],
				timeFrom: '',
				timeTo: '',
				exdate: [],
				priority: false,
				interruptsMusic: false,
				type: 'other' as const,
			}
		}

		// Fresh create flow defaults
		return {
			name: '',
			rruleType: 'recurrent',
			startDate: undefined as unknown as Date,
			endDate: undefined as unknown as Date,
			playEvery: 30,
			playEveryUnit: 'minutes',
			selectedDays: ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'],
			timeFrom: '00:00',
			timeTo: '23:00',
			exdate: [],
			priority: false,
			interruptsMusic: false,
			type: 'other' as const,
		}
	}, [isEdit, flowState, campaignId])

	const form = useForm<CampaignConfigFormValues>({
		resolver: yupResolver(campaignConfigSchema) as any,
		defaultValues,
		context: { isEdit },
	})

	// When editing and Redux state is already initialized with matching campaign,
	// use that state instead of fetching from API
	useEffect(() => {
		if (
			isEdit &&
			flowState.isInitialized &&
			flowState.mode === 'edit' &&
			flowState.campaignId === campaignId &&
			flowState.config &&
			!isFormInitialized
		) {
			form.reset(flowState.config)

			try {
				const rrule = generateRRule({
					rruleType: flowState.config.rruleType,
					playEvery: flowState.config.playEvery,
					playEveryUnit: flowState.config.playEveryUnit,
					selectedDays: (flowState.config.selectedDays || []).filter(
						(d): d is 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA' | 'SU' =>
							d !== undefined
					),
					timeFrom: flowState.config.timeFrom,
					timeTo: flowState.config.timeTo,
					startDate: flowState.config.startDate || undefined,
					endDate: flowState.config.endDate || undefined,
				})
				setCurrentRRule(rrule)
			} catch {
				setCurrentRRule(null)
			}

			setIsFormInitialized(true)
		}
	}, [isEdit, flowState, campaignId, form, isFormInitialized])

	// When editing, load the campaign from API and hydrate the form
	// Only runs when we need to fetch from API (shouldFetchFromAPI was true)
	// Wait for isFetching to be false to ensure we have fresh data, not stale cache
	useEffect(() => {
		if (
			isEdit &&
			campaign &&
			shouldFetchFromAPI &&
			!isFormInitialized &&
			!isFetching
		) {
			const values = mapCampaignToConfigFormValues(campaign)

			// Extra safety: make sure timeFrom/timeTo are always populated
			if (!values.timeFrom || !values.timeTo) {
				const byHourMatch = campaign.rrule?.match(/BYHOUR=([^;]+)/)
				if (byHourMatch && byHourMatch[1]) {
					const hours = byHourMatch[1]
						.split(',')
						.map((h) => parseInt(h, 10))
						.filter((h) => !Number.isNaN(h))

					if (hours.length > 0) {
						const fromHour = hours[0]
						const lastHour = hours[hours.length - 1]
						const toHour = Math.min(lastHour + 1, 23)

						values.timeFrom = `${String(fromHour).padStart(2, '0')}:00`
						values.timeTo = `${String(toHour).padStart(2, '0')}:00`
					}
				}
			}

			form.reset(values)

			// Initialize Redux state with API data
			dispatch(
				setCampaignFlow({
					mode: 'edit',
					config: values,
					campaignId: campaign.id,
					accountId: campaign.accountId,
					advertIds: campaign.adverts?.map((ad) => ad.id) || [],
					adverts: campaign.adverts,
				})
			)

			try {
				const rrule = generateRRule({
					rruleType: values.rruleType,
					playEvery: values.playEvery,
					playEveryUnit: values.playEveryUnit,
					selectedDays: (values.selectedDays || []).filter(
						(d): d is 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA' | 'SU' =>
							d !== undefined
					),
					timeFrom: values.timeFrom,
					timeTo: values.timeTo,
					startDate: values.startDate || undefined,
					endDate: values.endDate || undefined,
				})
				setCurrentRRule(rrule)
			} catch {
				setCurrentRRule(null)
			}

			setIsFormInitialized(true)
		}
	}, [
		isEdit,
		campaign,
		shouldFetchFromAPI,
		form,
		isFormInitialized,
		dispatch,
		isFetching,
	])

	// Keep RRULE preview in sync for both create and edit flows
	useEffect(() => {
		const subscription = form.watch((data) => {
			try {
				const rrule = generateRRule({
					rruleType: data.rruleType || 'recurrent',
					playEvery: data.playEvery,
					playEveryUnit: data.playEveryUnit,
					selectedDays: (data.selectedDays || []).filter(
						(d): d is 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA' | 'SU' =>
							d !== undefined
					),
					timeFrom: data.timeFrom,
					timeTo: data.timeTo,
					startDate: data.startDate || undefined,
					endDate: data.endDate || undefined,
				})
				setCurrentRRule(rrule)
			} catch {
				setCurrentRRule(null)
			}
		})

		return () => subscription.unsubscribe()
	}, [form])

	const onSubmit = (values: CampaignConfigFormValues) => {
		if (isReadOnly) return
		const finalValues: CampaignConfigFormValues = {
			...values,
			type: values.interruptsMusic ? 'opening' : 'other',
		}

		if (isEdit && campaignId) {
			// Update Redux state with new config, preserve other flow data
			dispatch(
				setCampaignFlow({
					mode: 'edit',
					config: finalValues,
					campaignId: flowState.campaignId || campaignId,
					accountId: flowState.accountId,
					advertIds: flowState.advertIds,
					adverts: flowState.adverts,
				})
			)

			navigate(`/campaigns/${campaignId}/edit/upload`)
		} else {
			// Create mode - update or initialize Redux state
			dispatch(
				setCampaignFlow({
					mode: 'create',
					config: finalValues,
					advertIds: flowState.advertIds,
					adverts: flowState.adverts,
				})
			)

			navigate('/campaigns/create/upload')
		}
	}

	const handleCancel = () => {
		navigate('/campaigns')
	}

	const logCampaignRRule = () => {
		console.log('Campaign RRULE:', currentRRule)
	}

	const rruleType = form.watch('rruleType')
	const exdate = form.watch('exdate') as (string | undefined)[] | undefined
	const startDate = (form.watch('startDate') || undefined) as Date | undefined
	const endDate = (form.watch('endDate') || undefined) as Date | undefined

	const heading = isReadOnly
		? (form.watch('name') as string) || ''
		: isEdit
			? t('config.editTitle')
			: t('config.createTitle')
	const isLoading =
		isEdit &&
		(!campaign || !isFormInitialized || isFetching) &&
		shouldFetchFromAPI

	return (
		<>
			<Dialog
				open={accessDeniedModalOpen}
				onOpenChange={(open) => {
					if (!open) {
						setAccessDeniedModalOpen(false)
						navigate(redirectToRef.current, { replace: true })
						return
					}
					setAccessDeniedModalOpen(true)
				}}
			>
				<DialogContent className="sm:max-w-[520px]">
					<DialogHeader>
						<DialogTitle>{tCommon('campaigns.accessDenied.title')}</DialogTitle>
						<DialogDescription>
							{tCommon('campaigns.accessDenied.description')}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => window.open('https://wa.me/15557052438', '_blank')}
						>
							{tCommon('actions.contactSupport')}
						</Button>
						<Button
							onClick={() => {
								setAccessDeniedModalOpen(false)
								navigate(redirectToRef.current, { replace: true })
							}}
						>
							{tCommon('actions.close')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<CampaignDetailsPageUI
				heading={heading}
				isEdit={isEdit}
				isLoading={isLoading}
				form={form}
				currentRRule={currentRRule}
				exdate={(exdate || []).filter((d): d is string => d !== undefined)}
				rruleType={rruleType}
				startDate={startDate}
				endDate={endDate}
				onSubmit={onSubmit}
				onCancel={handleCancel}
				onHeadingClick={logCampaignRRule}
				readOnly={isReadOnly}
				canEditCampaign={canEditCampaign}
				canDeleteCampaign={canDeleteCampaign}
				campaignId={campaignId}
				initialGroupIds={campaign?.groups?.map((group) => group.id) ?? []}
			/>
		</>
	)
}

export default CampaignDetailsPage
